import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link, Navigate } from 'react-router-dom';
import {
  ChevronLeft, Plus, Users, MapPin, Clock,
  Wifi, WifiOff, LocateFixed, Loader2, AlertTriangle,
  Mail, Phone, Pencil, Trash2, Building2, Upload, Search, FileSpreadsheet,
  Briefcase, X as XIcon,
} from 'lucide-react';
import {
  searchCompanies, getCompanyWorkers, addWorker, updateWorker,
  updateCompany, deleteCompany,
  getWorkplaces, createWorkplace, updateWorkplace, deleteWorkplace,
} from '../services/api';
import WorkerModal from '../components/WorkerModal';
import CompanyModal from '../components/CompanyModal';
import WorkplaceModal from '../components/WorkplaceModal';
import ConfirmDialog from '../components/ConfirmDialog';
import BulkImportModal from '../components/BulkImportModal';
import BulkEventImportModal from '../components/BulkEventImportModal';
import BulkAssignAgreementModal from '../components/BulkAssignAgreementModal';
import CompanyHolidaysSection from '../components/CompanyHolidaysSection';
import { USER_TYPE_LABELS, can, filterWorkersForUser } from '../lib/permissions';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import { FEEDBACK } from '../services/feedbackMessages';
import { verifyWorkerSave, findSavedWorker } from '../utils/workerSaveCheck';

const ErrorBanner = ({ error, onRetry }) => (
  <div className="border border-danger/30 bg-danger/5 rounded-xl p-4 mb-5">
    <div className="flex items-center gap-2 mb-1">
      <AlertTriangle size={16} className="text-danger" />
      <span className="text-xs font-bold uppercase tracking-wider text-danger">Error de API</span>
    </div>
    <div className="text-sm font-bold text-ink">{error.message}</div>
    {error.url && <div className="text-xs text-ink-muted font-mono mt-1">{error.url}</div>}
    {onRetry && (
      <button onClick={onRetry} className="mt-2 text-xs font-semibold text-danger hover:underline">Reintentar</button>
    )}
  </div>
);

const CompanyDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const companyId = Number(id);

  const [company, setCompany] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [workplaces, setWorkplaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const toast = useToast();

  const { user: authUser } = useAuth();
  const canBulkImport = can(authUser?.type, 'bulkImport');
  const canEditCompany = can(authUser?.type, 'manageOwnCompany');
  const canDeleteCompany = can(authUser?.type, 'manageEmpresas');
  const canManageSedes = can(authUser?.type, 'manageWorkplaces');
  const canDeleteSede = can(authUser?.type, 'deleteWorkplace');
  const canAddWorker = can(authUser?.type, 'manageWorkers');
  // Festivos de empresa: gestionan admin empresa (la suya — isOutOfScope ya
  // bloquea las ajenas) y super. El inspector (4) los ve en sólo lectura.
  const canManageCompanyHolidays = can(authUser?.type, 'manageCompanyHolidays');
  // Desactivado hasta que el backend de producción tenga /api/companyHoliday
  // (hoy responde 500). Poner a true cuando esté desplegado.
  const COMPANY_HOLIDAYS_ENABLED = false;
  const canViewCompanyHolidays = COMPANY_HOLIDAYS_ENABLED && (canManageCompanyHolidays || authUser?.type === 4);

  // Restricción duro: admin empresa y admin sede sólo pueden entrar a SU empresa.
  const isOutOfScope = (authUser?.type === 1 || authUser?.type === 2)
    && Number(authUser?.companyId) !== companyId;

  const [workerModal, setWorkerModal] = useState({ open: false, initial: null });
  const [companyModal, setCompanyModal] = useState(false);
  const [workplaceModal, setWorkplaceModal] = useState({ open: false, initial: null });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkEventsOpen, setBulkEventsOpen] = useState(false);
  const [deleteCompanyDialog, setDeleteCompanyDialog] = useState(false);
  const [deleteWorkplaceDialog, setDeleteWorkplaceDialog] = useState({ open: false, workplace: null });
  const [workerQuery, setWorkerQuery] = useState('');
  const [workerTab, setWorkerTab] = useState('activos'); // activos | inactivos
  // Asignación masiva de convenios — SOLO super admin. Set<workerId> con
  // la selección y modal para lanzar el alta. La selección se limpia al
  // completar la operación.
  const isMasterUser = authUser?.type === 5;
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAgreementOpen, setBulkAgreementOpen] = useState(false);

  // `loading` solo en la PRIMERA carga (o al cambiar de empresa). Las
  // recargas posteriores (tras guardar, tras una importación…) son
  // silenciosas: si pusiéramos el spinner de página completa se desmontaría
  // todo el árbol, incluidos los modales abiertos, y el usuario perdería el
  // informe de la importación masiva sin ver ningún error.
  const loadedCompanyIdRef = useRef(null);

  const load = useCallback(async () => {
    const firstLoad = loadedCompanyIdRef.current !== companyId;
    if (firstLoad) setLoading(true);
    setError(null);
    try {
      const companies = await searchCompanies('');
      const found = (Array.isArray(companies) ? companies : []).find((c) => c.id === companyId);
      // Mantenemos el valor anterior mientras llega el nuevo; solo se anula
      // cuando la empresa realmente no existe.
      setCompany(found || null);
    } catch (err) {
      setError(err);
    }
    try {
      const w = await getCompanyWorkers(companyId);
      setWorkers(Array.isArray(w) ? w : []);
    } catch (err) {
      setError(err);
      setWorkers([]);
    }
    try {
      const wp = await getWorkplaces(companyId);
      setWorkplaces(Array.isArray(wp) ? wp : []);
    } catch (err) {
      setWorkplaces([]);
    }
    loadedCompanyIdRef.current = companyId;
    setLoading(false);
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  // Confirmaciones con toast (components/Toast). Los ERRORES de estos
  // handlers los enseñan los propios modales / ConfirmDialog, que siguen
  // abiertos para reintentar: aquí solo va el éxito.
  //
  // Devuelve el worker resultante (en alta el creado, con su id; en edición
  // el original con el payload mergeado), para que WorkerModal pueda usar
  // ese id al guardar la jornada semanal.
  const handleAddWorker = async (payload) => {
    let saved;
    const isEdit = !!workerModal.initial?.id;
    if (isEdit) {
      saved = await updateWorker(workerModal.initial.id, payload);
    } else {
      saved = await addWorker(payload);
    }

    // El 200 OK del PUT/POST NO basta: hay campos que el servidor ignora en
    // silencio (isLoginEnabled con empresas sin límite de licencias — ver
    // utils/workerSaveCheck.js). Releemos la ficha y comparamos: si algo no ha
    // cuajado, el aviso SUSTITUYE al "guardado" en verde. Si la relectura
    // falla, el aviso dice que no se ha podido verificar — nunca rompe el
    // guardado, que a estas alturas ya está hecho.
    const workerId = workerModal.initial?.id || saved?.id || null;
    const check = await verifyWorkerSave({
      workerName: payload.name,
      created: !isEdit,
      sent: payload,
      company,
      readWorker: async () => findSavedWorker(
        await getCompanyWorkers(companyId),
        { id: workerId, email: payload.email, nif: payload.nif },
      ),
    });
    if (check.level === 'warning') {
      // Sin auto-cierre: esto hay que leerlo entero antes de seguir.
      toast.warning(check.message, { title: 'Guardado con avisos', duration: 0 });
    } else {
      toast.success(FEEDBACK.workerSaved({ name: payload.name, created: !isEdit }));
    }

    await load();
    return saved && saved.id ? saved : { ...(workerModal.initial || {}), ...payload };
  };

  const handleSaveCompany = async (payload) => {
    await updateCompany(companyId, payload);
    toast.success(FEEDBACK.companySaved({ name: payload.name, created: false }));
    await load();
  };

  const handleDeleteCompany = async () => {
    await deleteCompany(companyId);
    toast.success(FEEDBACK.companyDeleted(company?.name));
    navigate('/panel/empresas');
  };

  const handleSaveWorkplace = async (payload) => {
    const isEdit = !!workplaceModal.initial?.id;
    if (isEdit) {
      await updateWorkplace(workplaceModal.initial.id, payload);
    } else {
      await createWorkplace(companyId, payload);
    }
    toast.success(FEEDBACK.workplaceSaved({ name: payload.name, created: !isEdit }));
    await load();
  };

  const handleDeleteWorkplace = async () => {
    const wp = deleteWorkplaceDialog.workplace;
    await deleteWorkplace(wp.id);
    toast.success(FEEDBACK.workplaceDeleted(wp?.name));
    setDeleteWorkplaceDialog({ open: false, workplace: null });
    await load();
  };

  const workplaceName = (id) => workplaces.find((w) => w.id === id)?.name || (id ? `#${id}` : '—');

  // Para admin sede: workplaces y workers se restringen a su workingSite.
  const targetSite = authUser?.workingSite != null ? String(authUser.workingSite) : null;
  const visibleWorkplaces = (authUser?.type === 2 && targetSite)
    ? workplaces.filter((wp) => String(wp.id) === targetSite)
    : workplaces;
  const scopedWorkers = filterWorkersForUser(workers, authUser);
  const isWorkerActive = (w) => w.isLoginEnabled !== false;
  const tabWorkers = scopedWorkers.filter((w) =>
    workerTab === 'inactivos' ? !isWorkerActive(w) : isWorkerActive(w)
  );
  const visibleWorkers = workerQuery
    ? tabWorkers.filter((w) =>
        `${w.name || ''} ${w.email || ''} ${w.nif || ''}`
          .toLowerCase()
          .includes(workerQuery.toLowerCase())
      )
    : tabWorkers;
  const inactiveWorkersCount = scopedWorkers.filter((w) => !isWorkerActive(w)).length;
  const activeWorkersCount = scopedWorkers.length - inactiveWorkersCount;
  const totalWorkersCount = scopedWorkers.length;
  const activeWorkersPct = totalWorkersCount === 0 ? 0 : Math.round((activeWorkersCount / totalWorkersCount) * 100);
  const inactiveWorkersPct = totalWorkersCount === 0 ? 0 : 100 - activeWorkersPct;
  // Licencias de la empresa: 0 = sin límite. Si hay tope y los activos lo
  // alcanzan, el backend creará a los nuevos como inactivos automáticamente.
  const licenseCap = Number(company?.licenses) || 0;
  const licensesAvailable = licenseCap > 0 ? Math.max(0, licenseCap - activeWorkersCount) : null;
  const licensesExhausted = licenseCap > 0 && activeWorkersCount >= licenseCap;

  if (isOutOfScope) {
    return <Navigate to="/panel" replace />;
  }

  if (loading) {
    return (
      <div className="h-[calc(100vh-64px-4rem)] flex items-center justify-center">
        <Loader2 size={24} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-64px-4rem)] flex flex-col min-h-0">
      <div className="mb-5">
        {can(authUser?.type, 'viewEmpresas') && (
          <Link to="/panel/empresas" className="inline-flex items-center gap-1 text-sm font-semibold text-ink-muted hover:text-ink transition-colors mb-3">
            <ChevronLeft size={16} /> Volver a Empresas
          </Link>
        )}
        {company ? (
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-1">Empresa #{companyId}</div>
              <h1 className="font-display text-2xl md:text-3xl font-bold text-ink">{company.name}</h1>
              <div className="flex flex-wrap gap-4 mt-2 text-sm text-ink-muted">
                <span className="font-mono">{company.cif}</span>
                <span>{company.responsible}</span>
                {company.email && (
                  <a href={`mailto:${company.email}`} className="inline-flex items-center gap-1 hover:text-primary">
                    <Mail size={12} /> {company.email}
                  </a>
                )}
                {company.phone && (
                  <a href={`tel:${company.phone}`} className="inline-flex items-center gap-1 hover:text-primary">
                    <Phone size={12} /> {company.phone}
                  </a>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {canBulkImport && (
                <button
                  onClick={() => setBulkOpen(true)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-primary-darker border border-primary/30 hover:bg-primary-soft transition-colors"
                  title="Crear sedes y trabajadores masivamente desde un Excel"
                >
                  <Upload size={14} /> Importación masiva
                </button>
              )}
              {canBulkImport && (
                <button
                  onClick={() => setBulkEventsOpen(true)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-primary-darker border border-primary/30 hover:bg-primary-soft transition-colors"
                  title="Importar eventos de fichaje desde Excel de Intratime"
                >
                  <FileSpreadsheet size={14} /> Importar fichajes
                </button>
              )}
              {canEditCompany && (
                <button
                  onClick={() => setCompanyModal(true)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-ink border border-slate-200 hover:border-primary hover:text-primary transition-colors"
                >
                  <Pencil size={14} /> Editar
                </button>
              )}
              {canDeleteCompany && (
                <button
                  onClick={() => setDeleteCompanyDialog(true)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-danger border border-danger/30 hover:bg-danger/5 transition-colors"
                >
                  <Trash2 size={14} /> Eliminar
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="text-ink-muted">Empresa #{companyId} no encontrada.</div>
        )}
      </div>

      {error && <ErrorBanner error={error} onRetry={load} />}

      {!loading && company && workplaces.length === 0 && canManageSedes && (
        <div className="border border-warning/40 bg-warning/5 rounded-xl p-3 mb-4 flex items-start gap-3">
          <AlertTriangle size={18} className="text-warning shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <div className="font-semibold text-ink">Esta empresa no tiene ninguna sede.</div>
            <div className="text-xs text-ink-muted mt-1">
              Toda empresa debe tener al menos una sede para que los trabajadores con geolocalización puedan fichar.
              {' '}
              <button onClick={() => setWorkplaceModal({ open: true, initial: null })} className="font-semibold text-primary-darker hover:underline">
                Añadir la primera sede
              </button>.
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 grid lg:grid-cols-[1fr_340px] gap-6 overflow-hidden">
        <section className="flex flex-col min-h-0">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold inline-flex items-center gap-2">
              <Users size={14} /> Trabajadores
            </div>
            <div className="flex items-center gap-2">
              {canAddWorker && (
                <button
                  onClick={() => setWorkerModal({ open: true, initial: null })}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary text-white hover:bg-primary-dark transition-colors"
                >
                  <Plus size={12} /> Añadir
                </button>
              )}
            </div>
          </div>
          {licenseCap > 0 && (
            <div className={`mb-3 flex items-center gap-2 px-3 py-2 rounded-lg border ${
              licensesExhausted
                ? 'bg-warning/10 border-warning/30 text-warning'
                : 'bg-primary-soft/40 border-primary/20 text-primary-darker'
            }`}>
              <Users size={14} className="shrink-0" />
              <div className="text-xs font-semibold flex-1">
                {activeWorkersCount} / {licenseCap} empleados activos
                {licensesExhausted ? (
                  <span className="ml-2 font-normal">· Cupo alcanzado. Los nuevos empleados se crearán como inactivos.</span>
                ) : (
                  <span className="ml-2 font-normal text-ink-muted">· {licensesAvailable} licencia{licensesAvailable === 1 ? '' : 's'} disponible{licensesAvailable === 1 ? '' : 's'}</span>
                )}
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <div className="inline-flex items-center gap-1 border-b border-slate-200">
              {[
                { key: 'activos', label: 'Activos', count: activeWorkersCount, pct: activeWorkersPct },
                { key: 'inactivos', label: 'Inactivos', count: inactiveWorkersCount, pct: inactiveWorkersPct },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => setWorkerTab(t.key)}
                  className={`px-3 py-1.5 text-xs font-semibold border-b-2 -mb-px transition-colors ${
                    workerTab === t.key
                      ? 'border-primary text-primary-darker'
                      : 'border-transparent text-ink-muted hover:text-ink'
                  }`}
                >
                  {t.label}
                  <span className="ml-1 text-ink-muted tabular-nums">{t.count}</span>
                  {totalWorkersCount > 0 && (
                    <span className="ml-1 text-ink-muted/70 tabular-nums">· {t.pct}%</span>
                  )}
                </button>
              ))}
            </div>
            <div className="relative flex-1 min-w-[180px] max-w-md">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
              <input
                type="text"
                value={workerQuery}
                onChange={(e) => setWorkerQuery(e.target.value)}
                placeholder="Buscar por nombre, email o NIF…"
                className="w-full pl-8 pr-3 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-xs"
              />
            </div>
          </div>
          <div className="border-t border-slate-200 flex-1 min-h-0 overflow-y-auto">
            {visibleWorkers.length === 0 ? (
              <div className="py-12 text-center text-sm text-ink-muted">Sin trabajadores.</div>
            ) : (
              <table className="w-full min-w-[600px]">
                <thead className="sticky top-0 bg-background z-10">
                  <tr className="text-xs uppercase tracking-wider text-ink-muted">
                    {isMasterUser && (
                      <th className="text-center font-semibold py-3 pr-2 pl-2 w-8">
                        <input
                          type="checkbox"
                          checked={visibleWorkers.length > 0 && visibleWorkers.every((w) => selectedIds.has(w.id))}
                          onChange={(e) => {
                            const next = new Set(selectedIds);
                            if (e.target.checked) visibleWorkers.forEach((w) => next.add(w.id));
                            else visibleWorkers.forEach((w) => next.delete(w.id));
                            setSelectedIds(next);
                          }}
                          title="Seleccionar todos los visibles"
                        />
                      </th>
                    )}
                    <th className="text-left font-semibold py-3 pr-4">Nombre</th>
                    <th className="text-left font-semibold py-3 pr-4 hidden md:table-cell">Rol</th>
                    <th className="text-left font-semibold py-3 pr-4 hidden lg:table-cell">Sede</th>
                    <th className="text-right font-semibold py-3 pr-4 w-24">Opciones</th>
                    <th className="text-right font-semibold py-3 pr-2 w-12"></th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {visibleWorkers.map((w) => (
                    <tr key={w.id} className={`border-t border-slate-100 hover:bg-white transition-colors ${
                      selectedIds.has(w.id) ? 'bg-primary-soft/40' : ''
                    }`}>
                      {isMasterUser && (
                        <td className="py-2.5 pr-2 pl-2 text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(w.id)}
                            onChange={(e) => {
                              const next = new Set(selectedIds);
                              if (e.target.checked) next.add(w.id);
                              else next.delete(w.id);
                              setSelectedIds(next);
                            }}
                            title={`Seleccionar ${w.name}`}
                          />
                        </td>
                      )}
                      <td className="py-2.5 pr-4">
                        <div className="font-semibold text-ink">{w.name}</div>
                        <div className="text-xs text-ink-muted">{w.email}</div>
                      </td>
                      <td className="py-2.5 pr-4 text-ink-muted hidden md:table-cell">
                        {w.type != null ? USER_TYPE_LABELS[w.type] : <span className="italic text-ink-muted/60">sin asignar</span>}
                      </td>
                      <td className="py-2.5 pr-4 hidden lg:table-cell">
                        <span className="inline-flex items-center gap-1.5 text-xs text-ink-muted">
                          <MapPin size={12} /> {workplaceName(Number(w.workingSite))}
                        </span>
                      </td>
                      <td className="py-2.5 pr-4 text-right">
                        <div className="inline-flex items-center gap-1">
                          {w.geoTracking && <span title="Geolocalización" className="p-1 text-primary"><LocateFixed size={12} /></span>}
                          {w.worksRemotely ? <span title="Remoto" className="p-1 text-success"><Wifi size={12} /></span> : <span title="Presencial" className="p-1 text-ink-muted"><WifiOff size={12} /></span>}
                          {w.canRequestCheckins && <span title="Fichaje diferido" className="p-1 text-warning"><Clock size={12} /></span>}
                        </div>
                      </td>
                      <td className="py-2.5 pr-2 text-right">
                        <button onClick={() => setWorkerModal({ open: true, initial: w })} className="p-1.5 rounded text-ink-muted hover:text-primary hover:bg-slate-100 transition-colors" title="Editar">
                          <Pencil size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        <aside className="flex flex-col min-h-0 gap-5">
          <div className="flex flex-col min-h-0 flex-1">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold inline-flex items-center gap-2">
              <Building2 size={14} /> Sedes
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-ink-muted">{visibleWorkplaces.length}</span>
              {canManageSedes && (
                <button
                  onClick={() => setWorkplaceModal({ open: true, initial: null })}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-ink text-white hover:bg-slate-800 transition-colors"
                >
                  <Plus size={12} /> Añadir
                </button>
              )}
            </div>
          </div>
          <div className="border-t border-slate-200 flex-1 min-h-0 overflow-y-auto">
            {visibleWorkplaces.length === 0 ? (
              <div className="py-8 text-center text-xs text-ink-muted">Sin sedes registradas.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {visibleWorkplaces.map((w) => (
                  <li key={w.id} className="py-3 flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-ink truncate">{w.name}</div>
                      <div className="text-xs text-ink-muted truncate">
                        {w.email || '—'} · {w.phone || '—'}
                      </div>
                    </div>
                    <div className="inline-flex items-center gap-0.5 shrink-0">
                      {canManageSedes && (
                        <button onClick={() => setWorkplaceModal({ open: true, initial: w })} className="p-1.5 rounded text-ink-muted hover:text-primary hover:bg-slate-100 transition-colors" title="Editar">
                          <Pencil size={13} />
                        </button>
                      )}
                      {canDeleteSede && (
                        <button onClick={() => setDeleteWorkplaceDialog({ open: true, workplace: w })} className="p-1.5 rounded text-ink-muted hover:text-danger hover:bg-slate-100 transition-colors" title="Eliminar">
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          </div>

          {canViewCompanyHolidays && company && (
            <div className="shrink-0 max-h-[60%] overflow-y-auto pr-1">
              <CompanyHolidaysSection
                companyId={companyId}
                companyName={company.name}
                canManage={canManageCompanyHolidays}
              />
            </div>
          )}
        </aside>
      </div>

      <WorkerModal
        open={workerModal.open}
        companyId={companyId}
        initial={workerModal.initial}
        workplaces={workplaces}
        onClose={() => setWorkerModal({ open: false, initial: null })}
        onSubmit={handleAddWorker}
      />

      <CompanyModal
        open={companyModal}
        initial={company}
        onClose={() => setCompanyModal(false)}
        onSubmit={handleSaveCompany}
      />

      <WorkplaceModal
        open={workplaceModal.open}
        initial={workplaceModal.initial}
        onClose={() => setWorkplaceModal({ open: false, initial: null })}
        onSubmit={handleSaveWorkplace}
      />

      <BulkImportModal
        open={bulkOpen}
        company={company}
        onClose={() => setBulkOpen(false)}
        onDone={load}
      />

      <BulkEventImportModal
        open={bulkEventsOpen}
        companyId={companyId}
        onClose={() => setBulkEventsOpen(false)}
        onDone={load}
      />

      {/* Barra inferior fija para acciones masivas (super admin con N>0). */}
      {isMasterUser && selectedIds.size > 0 && !bulkAgreementOpen && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-white border border-primary/30 shadow-2xl rounded-full pl-4 pr-2 py-2 flex items-center gap-3">
          <span className="text-sm font-semibold text-ink tabular-nums">
            {selectedIds.size} seleccionado{selectedIds.size === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            onClick={() => setBulkAgreementOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-white bg-primary hover:bg-primary-dark transition-colors"
          >
            <Briefcase size={12} /> Asignar convenio
          </button>
          <button
            type="button"
            onClick={() => setSelectedIds(new Set())}
            className="inline-flex items-center justify-center w-8 h-8 rounded-full text-ink-muted hover:text-ink hover:bg-slate-100 transition-colors"
            title="Limpiar selección"
          >
            <XIcon size={14} />
          </button>
        </div>
      )}

      {isMasterUser && (
        <BulkAssignAgreementModal
          open={bulkAgreementOpen}
          workers={visibleWorkers.filter((w) => selectedIds.has(w.id))}
          onClose={() => setBulkAgreementOpen(false)}
          onCompleted={() => { setSelectedIds(new Set()); load(); }}
        />
      )}

      <ConfirmDialog
        open={deleteCompanyDialog}
        title="Eliminar empresa"
        message={`¿Seguro que quieres eliminar «${company?.name}»? Esta acción no se puede deshacer.`}
        warning="Se eliminarán en CASCADA todas las sedes, trabajadores y registros horarios vinculados a esta empresa."
        confirmText="Eliminar definitivamente"
        confirmWord={company?.cif || company?.name || 'ELIMINAR'}
        onClose={() => setDeleteCompanyDialog(false)}
        onConfirm={handleDeleteCompany}
      />

      <ConfirmDialog
        open={deleteWorkplaceDialog.open}
        title="Eliminar sede"
        message={`¿Eliminar la sede «${deleteWorkplaceDialog.workplace?.name}»?`}
        confirmText="Eliminar"
        onClose={() => setDeleteWorkplaceDialog({ open: false, workplace: null })}
        onConfirm={handleDeleteWorkplace}
      />
    </div>
  );
};

export default CompanyDetailPage;
